/**
 * Search Filters Component
 * Two-row search form with inline labels
 */

import React, {useEffect, useState, useCallback, useRef} from 'react';

import {Button} from '@pb/kit-component-button';
import {Calendar} from '@pb/kit-component-calendar';

import {
    filtersContainer,
    filterRow,
    filterField,
    label,
    input,
    select,
    dateRangeSeparator,
    dateRangeGroup,
    calendarWrapper,
    checkboxField,
    checkboxLabel,
    buttonField,
    controlRow,
    checkboxFieldInline,
    buttonFieldInline,
    toggleLinkActive,
    toggleLinkInactive,
    actionRow,
    actionRowRight,
    statusDropdown,
    statusDropdownTrigger,
    statusDropdownPanel,
    statusDropdownItem,
    statusDropdownItemAll,
    statusDropdownDivider,
    statusDropdownArrow,
} from './search-filters.styles.module.styl';

// Import generated OpenAPI types and API clients
import type {EventType, EventStatus} from '../../openapi/ccdb-bff/models';
import {fetchEventTypes, fetchStatuses} from '../../openapi/ccdb-bff/apis/EventDetailsApi';
import {searchEvents} from '../../openapi/ccdb-bff/apis/SearchEventsApi';
import {ROOT} from '../../middleware/api';

interface SearchFiltersProps {
    onSearch: (filters: SearchFiltersState) => void;
    onAddNew?: () => void;
    onToggleActive?: () => void;
    onToggleInactive?: () => void;
    toggleStatusLoading?: boolean;
    loading?: boolean;
}

export interface SearchFiltersState {
    searchText: string;
    searchType: string;
    eventType: string;
    eventGroup: string;
    coverage: string;
    eventDateStatus: string;
    status: string[];
    dateType: string;
    startDate: string;
    endDate: string;
    missingInfo: boolean;
}

/** Internal multi-checkbox dropdown for selecting one or more status values */
const StatusMultiSelect: React.FC<{
    options: EventStatus[];
    selected: string[];
    onChange: (values: string[]) => void;
    disabled: boolean;
}> = ({options, selected, onChange, disabled}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const allValues = options.map(o => o.status ?? '').filter(Boolean);
    const allSelected = allValues.length > 0 && allValues.every(v => selected.includes(v));
    const someSelected = !allSelected && selected.length > 0;

    const toggleValue = (value: string) => {
        if (selected.includes(value)) {
            onChange(selected.filter(v => v !== value));
        } else {
            onChange([...selected, value]);
        }
    };

    const toggleAll = () => {
        if (allSelected) {
            onChange([]);
        } else {
            onChange(allValues);
        }
    };

    const triggerLabel = selected.length === 0 ? '-- Select --'
        : allSelected ? 'All'
        : selected.join(', ');

    return (
        <div className={statusDropdown} ref={containerRef}>
            <button
                type="button"
                className={statusDropdownTrigger}
                onClick={() => { if (!disabled) setOpen(o => !o); }}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                title={triggerLabel}
            >
                <span>{triggerLabel}</span>
                <span className={statusDropdownArrow}>&#9662;</span>
            </button>
            {open && (
                <div className={statusDropdownPanel} role="listbox" aria-multiselectable="true">
                    {/* "All" select-all row */}
                    <label className={`${statusDropdownItem} ${statusDropdownItemAll}`}>
                        <input
                            type="checkbox"
                            checked={allSelected}
                            ref={el => { if (el) el.indeterminate = someSelected; }}
                            onChange={toggleAll}
                        />
                        <span><strong>All</strong></span>
                    </label>
                    <div className={statusDropdownDivider} />
                    {options.map(opt => (
                        <label key={opt.id} className={statusDropdownItem}>
                            <input
                                type="checkbox"
                                checked={selected.includes(opt.status ?? '')}
                                onChange={() => toggleValue(opt.status ?? '')}
                            />
                            <span>{opt.status}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};

// Placeholder dropdown options
const SEARCH_FIELD_OPTIONS = [
    {value: 'Company Name', label: 'Company Name'},
    {value: 'Company ID', label: 'Company ID'},
    {value: 'Symbol', label: 'Symbol'},
    {value: 'Event ID', label: 'Event ID'},
    {value: 'FF Priority', label: 'FF Priority'},
];

const EVENT_GROUP_OPTIONS = [
    {value: 'Company Meetings', label: 'Company Meetings'},
    {value: 'Conference Calls & Webcasts', label: 'Conference Calls & Webcasts'},
    {value: 'Financial Releases', label: 'Financial Releases'},
    {value: 'Unknown', label: 'Unknown'},
];

const COVERAGE_OPTIONS = [
    {value: 'US', label: 'US'},
    {value: 'UK', label: 'UK'},
    {value: 'Nordic', label: 'Nordic'},
    {value: 'Research', label: 'Research'},
    {value: 'Transcript', label: 'Transcript'},
];

const DATE_TYPE_OPTIONS = [
    {value: 'Effective Beginning Date', label: 'Effective Beginning Date'},
    {value: 'Modification Date', label: 'Modification Date'},
];

// Helper functions for date handling
const parseCalendarDate = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    try {
        // Handle MM/DD/YYYY format explicitly
        const parts = dateStr.split('/');
        if (parts.length === 3) {
            const month = parseInt(parts[0], 10) - 1;
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);
            
            // Validate parsed values
            if (isNaN(month) || isNaN(day) || isNaN(year)) {
                return null;
            }
            
            // Create date - JavaScript will auto-correct invalid dates
            const d = new Date(year, month, day);
            
            // Verify the date is valid (not NaN)
            if (isNaN(d.getTime())) {
                return null;
            }
            
            // Verify the parsed date matches input (prevent Nov 31 -> Dec 1 issues)
            if (d.getMonth() !== month || d.getDate() !== day || d.getFullYear() !== year) {
                return null;
            }
            
            return d;
        }
        return null;
    } catch {
        return null;
    }
};

const formatDateForStore = (date: Date | null | undefined): string => {
    if (!date) return '';
    if (isNaN(date.getTime())) return '';
    
    // Store in yyyy-MM-dd format for API
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const formatDateForCalendar = (dateStr: string): string => {
    if (!dateStr) return '';
    // Convert yyyy-MM-dd to MM/DD/YYYY for display in calendar
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
    }
    return dateStr;
};

const SearchFilters: React.FC<SearchFiltersProps> = ({onSearch, onAddNew, loading = false, onToggleActive, onToggleInactive, toggleStatusLoading = false}) => {
    const today = new Date();
    const minDate = new Date(today.getFullYear() - 50, 0, 1);
    const maxDate = new Date(today.getFullYear() + 50, 11, 31);

    const [filters, setFilters] = useState<SearchFiltersState>({
        searchText: '',
        searchType: '',
        eventType: '',
        eventGroup: '',
        coverage: '',
        eventDateStatus: '',
        status: [],
        dateType: '',
        startDate: '',
        endDate: '',
        missingInfo: false,
    });

    const [eventTypeOptions, setEventTypeOptions] = useState<EventType[]>([]);
    const [statusOptions, setStatusOptions] = useState<EventStatus[]>([]);
    const [eventDateStatusOptions, setEventDateStatusOptions] = useState<any[]>([]);
    const [loadingDropdowns, setLoadingDropdowns] = useState(false);

    // Fetch all dropdown options in parallel on mount so the form is immediately
    // interactive — no sequential awaits, no disabled state during loading.
    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                setLoadingDropdowns(true);

                // Fetch event types from generated API
                const etResponse = await fetch(`${ROOT}/api/event-types`, {
                    headers: {'Accept': 'application/json'},
                });
                if (etResponse.ok) {
                    const eventTypes = await etResponse.json() as EventType[];
                    setEventTypeOptions(Array.isArray(eventTypes) ? eventTypes : []);
                }

                // Fetch statuses from generated API
                const sResponse = await fetch(`${ROOT}/api/statuses`, {
                    headers: {'Accept': 'application/json'},
                });
                if (sResponse.ok) {
                    const data = await sResponse.json() as any;
                    const statuses = (data.objectData ?? data ?? []) as EventStatus[];
                    setStatusOptions(Array.isArray(statuses) ? statuses : []);
                }

                // Fetch event date statuses from API
                const edsResponse = await fetch(`${ROOT}/api/eventstatuses`, {
                    headers: {'Accept': 'application/json'},
                });
                if (edsResponse.ok) {
                    const edsData = await edsResponse.json() as any;
                    const eventDateStatuses = (edsData.objectData ?? edsData ?? []) as any[];
                    setEventDateStatusOptions(Array.isArray(eventDateStatuses) ? eventDateStatuses : []);
                }
            } catch (err) {
                console.error('Failed to fetch dropdowns:', err);
            } finally {
                setLoadingDropdowns(false);
            }
        };

        fetchDropdowns();
    }, []);

    const handleFilterChange = useCallback((field: keyof SearchFiltersState, value: any) => {
        setFilters(prev => ({...prev, [field]: value}));
    }, []);

    const handleSearchClick = useCallback(() => {
        onSearch(filters);
    }, [filters, onSearch]);

    const handleClearClick = useCallback(() => {
        setFilters({
            searchText: '',
            searchType: '',
            eventType: '',
            eventGroup: '',
            coverage: '',
            eventDateStatus: '',
            status: [],
            dateType: '0',
            startDate: '',
            endDate: '',
            missingInfo: false,
        });
    }, []);

    return (
        <div className={filtersContainer}>
            {/* Row 1: Search controls */}
            <div className={filterRow}>
                {/* Search Text */}
                <div className={filterField}>
                    <label htmlFor="search-text" className={label}>Search Text</label>
                    <input
                        id="search-text"
                        type="text"
                        placeholder="Enter search text"
                        value={filters.searchText}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('searchText', e.target.value)}
                        disabled={loading}
                        className={input}
                    />
                </div>

                {/* Search Field Select */}
                <div className={filterField}>
                    <label htmlFor="search-type" className={label}>Search Type</label>
                    <select
                        id="search-type"
                        value={filters.searchType}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('searchType', e.target.value)}
                        disabled={loading}
                        className={select}
                    >
                        <option value="">-- Select --</option>
                        {SEARCH_FIELD_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Event Type Select */}
                <div className={filterField}>
                    <label htmlFor="event-type" className={label}>Event Type</label>
                    <select
                        id="event-type"
                        value={filters.eventType}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('eventType', e.target.value)}
                        disabled={loading}
                        className={select}
                    >
                        <option value="">-- Select --</option>
                        {eventTypeOptions.map(opt => (
                            <option key={opt.eventTypeId} value={String(opt.eventTypeId)}>{opt.eventType}</option>
                        ))}
                    </select>
                </div>

                {/* Event Group Select */}
                <div className={filterField}>
                    <label htmlFor="event-group" className={label}>Event Group</label>
                    <select
                        id="event-group"
                        value={filters.eventGroup}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('eventGroup', e.target.value)}
                        disabled={loading}
                        className={select}
                    >
                        <option value="">-- Select --</option>
                        {EVENT_GROUP_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Coverage Select */}
                <div className={filterField}>
                    <label htmlFor="row1-coverage" className={label}>Coverage</label>
                    <select
                        id="row1-coverage"
                        value={filters.coverage}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('coverage', e.target.value)}
                        disabled={loading}
                        className={select}
                    >
                        <option value="">-- Select --</option>
                        {COVERAGE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Status — multi-checkbox dropdown */}
                <div className={filterField}>
                    <label className={label}>Status</label>
                    <StatusMultiSelect
                        options={statusOptions}
                        selected={filters.status}
                        onChange={values => handleFilterChange('status', values)}
                        disabled={loading}
                    />
                </div>
            </div>

            {/* Row 2: Date and detail filters */}
            <div className={filterRow}>
                {/* Event Date Status Select */}
                <div className={filterField}>
                    <label htmlFor="event-date-status" className={label}>Event Date Status</label>
                    <select
                        id="event-date-status"
                        value={filters.eventDateStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('eventDateStatus', e.target.value)}
                        disabled={loading}
                        className={select}
                    >
                        <option value="">-- Select --</option>
                        {eventDateStatusOptions.map(opt => (
                            <option key={opt.id} value={opt.eventStatus}>{opt.eventStatus}</option>
                        ))}
                    </select>
                </div>

                {/* Date Type Select */}
                <div className={filterField}>
                    <label htmlFor="date-type" className={label}>Date Type</label>
                    <select
                        id="date-type"
                        value={filters.dateType}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('dateType', e.target.value)}
                        disabled={loading}
                        className={select}
                    >
                        {DATE_TYPE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Date Range: From → separator → To spanning 2 grid columns */}
                <div className={filterField} style={{gridColumn: 'span 2'}}>
                    <label className={label}>Date Range</label>
                    <div className={dateRangeGroup}>
                        <div className={calendarWrapper}>
                            <Calendar
                                key={`start-${filters.startDate}`}
                                selected={parseCalendarDate(formatDateForCalendar(filters.startDate))}
                                onChange={(date?: Date) => handleFilterChange('startDate', formatDateForStore(date))}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                        <span className={dateRangeSeparator}>—</span>
                        <div className={calendarWrapper}>
                            <Calendar
                                key={`end-${filters.endDate}`}
                                selected={parseCalendarDate(formatDateForCalendar(filters.endDate))}
                                onChange={(date?: Date) => handleFilterChange('endDate', formatDateForStore(date))}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </div>
                    </div>
                </div>

                {/* Missing Info Checkbox */}
                <div className={filterField}>
                    <label className={label}>&nbsp;</label>
                    <div className={checkboxFieldInline}>
                        <input
                            id="missing-info"
                            type="checkbox"
                            checked={filters.missingInfo}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFilterChange('missingInfo', e.target.checked)}
                            disabled={loading}
                        />
                        <label htmlFor="missing-info" className={checkboxLabel}>Missing Info</label>
                    </div>
                </div>
            </div>

            {/* Row 3: Action row — Search/Clear left, Active/Inactive/Add New right */}
            <div className={actionRow}>
                <div className={buttonField}>
                    <Button onClick={handleSearchClick} disabled={loading}>
                        Search Event(s)
                    </Button>
                </div>
                <div className={buttonField}>
                    <Button onClick={handleClearClick} disabled={loading}>
                        Clear
                    </Button>
                </div>
                <div className={actionRowRight}>
                    {onToggleActive && (
                        <button
                            type="button"
                            className={toggleLinkActive}
                            onClick={onToggleActive}
                            disabled={loading || toggleStatusLoading}
                            aria-label="Set selected events to Active"
                        >
                            Active
                        </button>
                    )}
                    {onToggleInactive && (
                        <button
                            type="button"
                            className={toggleLinkInactive}
                            onClick={onToggleInactive}
                            disabled={loading || toggleStatusLoading}
                            aria-label="Set selected events to Inactive"
                        >
                            Inactive
                        </button>
                    )}
                    {onAddNew && (
                        <div className={buttonField}>
                            <Button onClick={onAddNew} disabled={loading}>
                                Add New Event
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchFilters;
